import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Sheet,
    Typography,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    Button,
    Box,
    Stack,
    Divider,
    Alert,
    IconButton
} from '@mui/joy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import domain from '../utils/domain';

interface ExamFormData {
    title: string;
    description: string;
    duration: number;
    totalMarks: number;
    passingMarks: number;
    negativeMarking: number;
    instructions: string;
}

const CreateExam: React.FC = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState<ExamFormData>({
        title: '',
        description: '',
        duration: 75,
        totalMarks: 75,
        passingMarks: 30,
        negativeMarking: 0.25,
        instructions: ''
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            const response = await fetch(`${domain}/api/admin/exams`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include',
                body: JSON.stringify(formData)
            });

            if (response.status === 401) {
                // Token expired or invalid
                localStorage.removeItem('adminToken');
                navigate('/admin');
                return;
            }

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to create exam');
            }

            // Navigate back to dashboard on success
            navigate('/admin/dashboard');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create exam');
            if (err instanceof Error && err.message === 'Authentication token not found') {
                navigate('/admin');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.level1', py: 4 }}>
            <Sheet
                variant="outlined"
                sx={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    p: 4,
                    borderRadius: 'lg',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <IconButton
                        onClick={() => navigate('/admin/dashboard')}
                        variant="plain"
                        size="sm"
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography level="h3">Create New Exam</Typography>
                </Box>

                {error && (
                    <Alert color="danger" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                        <FormControl required>
                            <FormLabel>Exam Title</FormLabel>
                            <Input
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="Enter exam title"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Description</FormLabel>
                            <Textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                minRows={3}
                                placeholder="Enter exam description"
                            />
                        </FormControl>

                        <Divider>Exam Settings</Divider>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                            <FormControl required>
                                <FormLabel>Duration (minutes)</FormLabel>
                                <Input
                                    type="number"
                                    name="duration"
                                    value={formData.duration}
                                    onChange={handleInputChange}
                                    slotProps={{
                                        input: {
                                            min: 1
                                        }
                                    }}
                                />
                            </FormControl>

                            <FormControl required>
                                <FormLabel>Total Marks</FormLabel>
                                <Input
                                    type="number"
                                    name="totalMarks"
                                    value={formData.totalMarks}
                                    onChange={handleInputChange}
                                    slotProps={{
                                        input: {
                                            min: 1
                                        }
                                    }}
                                />
                            </FormControl>

                            <FormControl required>
                                <FormLabel>Passing Marks</FormLabel>
                                <Input
                                    type="number"
                                    name="passingMarks"
                                    value={formData.passingMarks}
                                    onChange={handleInputChange}
                                    slotProps={{
                                        input: {
                                            min: 1
                                        }
                                    }}
                                />
                            </FormControl>

                            <FormControl required>
                                <FormLabel>Negative Marking</FormLabel>
                                <Input
                                    type="number"
                                    name="negativeMarking"
                                    value={formData.negativeMarking}
                                    onChange={handleInputChange}
                                    slotProps={{
                                        input: {
                                            min: 0,
                                            step: 0.25
                                        }
                                    }}
                                />
                            </FormControl>
                        </Box>

                        <FormControl>
                            <FormLabel>Instructions</FormLabel>
                            <Textarea
                                name="instructions"
                                value={formData.instructions}
                                onChange={handleInputChange}
                                minRows={4}
                                placeholder="Enter exam instructions"
                            />
                        </FormControl>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="plain"
                                color="neutral"
                                onClick={() => navigate('/admin/dashboard')}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                loading={isLoading}
                                loadingPosition="start"
                            >
                                Create Exam
                            </Button>
                        </Box>
                    </Stack>
                </form>
            </Sheet>
        </Box>
    );
};

export default CreateExam; 